/**
 * ESS POS — Separated Development Mock Adapter
 *
 * IMPORTANT ARCHITECTURAL BOUNDARY (Per Spec Section 7):
 * Core authentication, multi-tenant isolation, branches, catalog products,
 * stock balances, stock receipts, stock transfers, stock adjustments,
 * cashier shifts, POS sales checkout, billing, and financial reporting
 * communicate with the REAL Express backend API (`/api/v1/*`).
 *
 * This clearly separated adapter manages ONLY restaurant-specific workflows
 * awaiting dedicated backend endpoints:
 * 1. Waiter Floor Table Layout & Kitchen Order Status Queue (`/api/v1/restaurant/tables` & `/orders`)
 * 2. Waiter-to-Cashier Pending Table Bills handoff
 * 3. Recipe Ingredient Deduction Mappings & Supplier Directory
 * 4. Manager Discount / Void / Cancellation Approval Queue & Extended Audit Log
 *
 * All records in this adapter are strictly partitioned by `tenantId` so
 * one tenant can NEVER access another tenant's operational state.
 */

import {
  KitchenOrderStatus,
  ManagerApprovalRequest,
  RecipeIngredientMapping,
  RestaurantOrder,
  RestaurantOrderItem,
  RestaurantTable,
  SupplierRecord,
  TableStatus,
} from '../types/contracts';

interface TenantOperationalStore {
  tables: RestaurantTable[];
  orders: RestaurantOrder[];
  approvals: ManagerApprovalRequest[];
  suppliers: SupplierRecord[];
  recipes: RecipeIngredientMapping[];
  auditLogs: Array<{
    id: string;
    timestamp: string;
    actor: string;
    role: string;
    action: string;
    target: string;
    severity: 'INFO' | 'WARNING' | 'CRITICAL';
  }>;
}

class RestaurantMockAdapter {
  private stores = new Map<string, TenantOperationalStore>();

  private getStore(tenantId: string): TenantOperationalStore {
    if (!tenantId) {
      throw new Error('Tenant ID is required for isolated restaurant operations');
    }
    let store = this.stores.get(tenantId);
    if (!store) {
      store = this.createInitialTenantStore(tenantId);
      this.stores.set(tenantId, store);
    }
    return store;
  }

  private createInitialTenantStore(tenantId: string): TenantOperationalStore {
    const now = new Date();
    const minAgo = (m: number) => new Date(now.getTime() - m * 60 * 1000).toISOString();

    const tables: RestaurantTable[] = [
      {
        id: `tbl-${tenantId}-1`,
        tenantId,
        number: 'T-01',
        name: 'Table 01 (Window)',
        zone: 'Main Dining',
        capacity: 4,
        guests: 3,
        status: 'READY',
        assignedWaiterId: 'waiter-1',
        assignedWaiterName: 'Kevin Omondi',
        activeOrderId: `ord-${tenantId}-101`,
        currentBillCents: 249000,
        updatedAt: minAgo(4),
      },
      {
        id: `tbl-${tenantId}-2`,
        tenantId,
        number: 'T-02',
        name: 'Table 02 (Center)',
        zone: 'Main Dining',
        capacity: 2,
        guests: 0,
        status: 'AVAILABLE',
        assignedWaiterId: null,
        assignedWaiterName: null,
        activeOrderId: null,
        currentBillCents: 0,
        updatedAt: minAgo(35),
      },
      {
        id: `tbl-${tenantId}-3`,
        tenantId,
        number: 'T-03',
        name: 'Table 03 (Booth)',
        zone: 'Main Dining',
        capacity: 6,
        guests: 5,
        status: 'PREPARING',
        assignedWaiterId: 'waiter-1',
        assignedWaiterName: 'Kevin Omondi',
        activeOrderId: `ord-${tenantId}-102`,
        currentBillCents: 388000,
        updatedAt: minAgo(11),
      },
      {
        id: `tbl-${tenantId}-4`,
        tenantId,
        number: 'T-04',
        name: 'Table 04',
        zone: 'Main Dining',
        capacity: 4,
        guests: 0,
        status: 'AVAILABLE',
        assignedWaiterId: null,
        assignedWaiterName: null,
        activeOrderId: null,
        currentBillCents: 0,
        updatedAt: minAgo(50),
      },
      {
        id: `tbl-${tenantId}-5`,
        tenantId,
        number: 'G-01',
        name: 'Garden Pergola 1',
        zone: 'Garden Terrace',
        capacity: 6,
        guests: 4,
        status: 'AWAITING_PAYMENT',
        assignedWaiterId: 'waiter-1',
        assignedWaiterName: 'Kevin Omondi',
        activeOrderId: `ord-${tenantId}-103`,
        currentBillCents: 342000,
        updatedAt: minAgo(2),
      },
      {
        id: `tbl-${tenantId}-6`,
        tenantId,
        number: 'G-02',
        name: 'Garden Pergola 2',
        zone: 'Garden Terrace',
        capacity: 4,
        guests: 2,
        status: 'ORDERING',
        assignedWaiterId: 'waiter-1',
        assignedWaiterName: 'Kevin Omondi',
        activeOrderId: null,
        currentBillCents: 0,
        updatedAt: minAgo(6),
      },
      {
        id: `tbl-${tenantId}-7`,
        tenantId,
        number: 'V-01',
        name: 'VIP Boardroom Table',
        zone: 'VIP Lounge',
        capacity: 10,
        guests: 0,
        status: 'AVAILABLE',
        assignedWaiterId: null,
        assignedWaiterName: null,
        activeOrderId: null,
        currentBillCents: 0,
        updatedAt: minAgo(90),
      },
      {
        id: `tbl-${tenantId}-8`,
        tenantId,
        number: 'V-02',
        name: 'VIP Private Booth',
        zone: 'VIP Lounge',
        capacity: 6,
        guests: 4,
        status: 'OCCUPIED',
        assignedWaiterId: 'waiter-2',
        assignedWaiterName: 'Brian Kiprop',
        activeOrderId: null,
        currentBillCents: 0,
        updatedAt: minAgo(8),
      },
    ];

    const orders: RestaurantOrder[] = [
      {
        id: `ord-${tenantId}-101`,
        orderNumber: 'KOT-2041',
        tenantId,
        tableId: `tbl-${tenantId}-1`,
        tableName: 'T-01 • Table 01 (Window)',
        waiterName: 'Kevin Omondi',
        guests: 3,
        status: 'READY',
        items: [
          {
            id: 'oi-1',
            variantId: 'seed-v1',
            productName: 'Nyama Choma Mbuzi Platter (1kg)',
            sku: 'GRL-001',
            quantity: 1,
            unitPriceCents: 185000,
            notes: 'Well done, extra kachumbari on the side',
          },
          {
            id: 'oi-2',
            variantId: 'seed-v2',
            productName: 'Fresh Passion & Mint Cooler (500ml)',
            sku: 'DRK-001',
            quantity: 2,
            unitPriceCents: 32000,
            notes: 'Less ice',
          },
        ],
        subtotalCents: 249000,
        taxCents: 0,
        totalCents: 249000,
        submittedAt: minAgo(18),
        updatedAt: minAgo(4),
      },
      {
        id: `ord-${tenantId}-102`,
        orderNumber: 'KOT-2042',
        tenantId,
        tableId: `tbl-${tenantId}-3`,
        tableName: 'T-03 • Table 03 (Booth)',
        waiterName: 'Kevin Omondi',
        guests: 5,
        status: 'PREPARING',
        items: [
          {
            id: 'oi-3',
            variantId: 'seed-v3',
            productName: 'Flame-Grilled T-Bone Steak (400g)',
            sku: 'GRL-002',
            quantity: 2,
            unitPriceCents: 165000,
            notes: 'Medium rare, black pepper sauce',
          },
          {
            id: 'oi-4',
            variantId: 'seed-v4',
            productName: 'Kenyan Ginger Dawa Tea',
            sku: 'HOT-001',
            quantity: 2,
            unitPriceCents: 28000,
            notes: 'Extra honey',
          },
        ],
        subtotalCents: 386000,
        taxCents: 0,
        totalCents: 386000,
        submittedAt: minAgo(11),
        updatedAt: minAgo(11),
      },
      {
        id: `ord-${tenantId}-103`,
        orderNumber: 'KOT-2039',
        tenantId,
        tableId: `tbl-${tenantId}-5`,
        tableName: 'G-01 • Garden Pergola 1',
        waiterName: 'Kevin Omondi',
        guests: 4,
        status: 'AWAITING_PAYMENT',
        items: [
          {
            id: 'oi-5',
            variantId: 'seed-v5',
            productName: 'Lake Victoria Whole Tilapia Wet Fry',
            sku: 'MNC-001',
            quantity: 2,
            unitPriceCents: 115000,
            notes: 'Mild coconut curry sauce',
          },
          {
            id: 'oi-6',
            variantId: 'seed-v6',
            productName: 'Masala Chips',
            sku: 'SDS-001',
            quantity: 2,
            unitPriceCents: 38000,
            notes: 'Crispy',
          },
          {
            id: 'oi-7',
            variantId: 'seed-v7',
            productName: 'Beef Samosas (Pair)',
            sku: 'SDS-002',
            quantity: 1,
            unitPriceCents: 28000,
            notes: 'With lime wedges',
          },
        ],
        subtotalCents: 334000,
        taxCents: 0,
        totalCents: 334000,
        submittedAt: minAgo(42),
        updatedAt: minAgo(2),
      },
    ];

    const approvals: ManagerApprovalRequest[] = [
      {
        id: `apr-${tenantId}-1`,
        tenantId,
        type: 'DISCOUNT',
        requestedBy: 'Grace Wanjiku',
        requesterRole: 'CASHIER',
        referenceCode: 'BILL-G01',
        amountCents: 30000,
        reason: 'Corporate loyalty guest — 10% courtesy dining discount',
        status: 'PENDING',
        createdAt: minAgo(14),
      },
      {
        id: `apr-${tenantId}-2`,
        tenantId,
        type: 'ORDER_CANCEL',
        requestedBy: 'Kevin Omondi',
        requesterRole: 'WAITER',
        referenceCode: 'KOT-2038',
        amountCents: 95000,
        reason: 'Guest changed order from Beef Burger to Whole Tilapia before kitchen prep',
        status: 'PENDING',
        createdAt: minAgo(25),
      },
    ];

    const suppliers: SupplierRecord[] = [
      {
        id: `sup-${tenantId}-1`,
        tenantId,
        name: 'Highlands Prime Meat Suppliers Ltd',
        category: 'Fresh Beef, Goat & Poultry',
        contactPerson: 'Peter Kariuki',
        phone: '+254 722 410 882',
        email: 'orders@highlandsmeat.co.ke',
        leadTimeDays: 1,
        status: 'ACTIVE',
      },
      {
        id: `sup-${tenantId}-2`,
        tenantId,
        name: 'Lake Basin Fresh Catch Distributors',
        category: 'Fresh Tilapia & Seafood',
        contactPerson: 'Millicent Otieno',
        phone: '+254 733 891 204',
        email: 'supply@lakebasincatch.co.ke',
        leadTimeDays: 1,
        status: 'ACTIVE',
      },
      {
        id: `sup-${tenantId}-3`,
        tenantId,
        name: 'Rift Valley Organic Produce & Spices',
        category: 'Vegetables, Potatoes, Herbs & Dry Goods',
        contactPerson: 'Samuel Koech',
        phone: '+254 710 553 910',
        email: 'sales@riftvalleyproduce.co.ke',
        leadTimeDays: 2,
        status: 'ACTIVE',
      },
    ];

    const recipes: RecipeIngredientMapping[] = [
      {
        id: `rec-${tenantId}-1`,
        tenantId,
        menuProductName: 'Savannah Gourmet Beef Burger & Fries',
        menuSku: 'MNC-002',
        ingredients: [
          { ingredientName: 'Brioche Burger Bun', unit: 'pcs', quantityPerPortion: 1, estimatedCostCents: 6000 },
          { ingredientName: 'Prime Ground Beef Patty (180g)', unit: 'g', quantityPerPortion: 180, estimatedCostCents: 21000 },
          { ingredientName: 'Aged Cheddar Cheese Slice', unit: 'slice', quantityPerPortion: 1, estimatedCostCents: 4500 },
          { ingredientName: 'House Burger Relish & Sauce', unit: 'ml', quantityPerPortion: 35, estimatedCostCents: 2500 },
          { ingredientName: 'Irish Potatoes (Cut Fries)', unit: 'g', quantityPerPortion: 220, estimatedCostCents: 8000 },
        ],
      },
      {
        id: `rec-${tenantId}-2`,
        tenantId,
        menuProductName: 'Nyama Choma Mbuzi Platter (1kg)',
        menuSku: 'GRL-001',
        ingredients: [
          { ingredientName: 'Prime Goat Ribs & Leg Cut', unit: 'g', quantityPerPortion: 1000, estimatedCostCents: 82000 },
          { ingredientName: 'Fresh Tomatoes, Onions & Coriander (Kachumbari)', unit: 'g', quantityPerPortion: 250, estimatedCostCents: 8000 },
          { ingredientName: 'ChefMarinade Herb & Garlic Rub', unit: 'ml', quantityPerPortion: 45, estimatedCostCents: 5000 },
        ],
      },
    ];

    const auditLogs = [
      {
        id: `aud-${tenantId}-1`,
        timestamp: minAgo(5),
        actor: 'Grace Wanjiku',
        role: 'CASHIER',
        action: 'POS_SALE_COMPLETED',
        target: 'Receipt RCP-MAIN-20261030-1088 (KES 2,290.00 via M-Pesa)',
        severity: 'INFO' as const,
      },
      {
        id: `aud-${tenantId}-2`,
        timestamp: minAgo(18),
        actor: 'Kevin Omondi',
        role: 'WAITER',
        action: 'KITCHEN_ORDER_SUBMITTED',
        target: 'Order KOT-2041 for Table T-01 (3 Guests)',
        severity: 'INFO' as const,
      },
      {
        id: `aud-${tenantId}-3`,
        timestamp: minAgo(45),
        actor: 'Peter Njoroge',
        role: 'INVENTORY_CLERK',
        action: 'LOW_STOCK_THRESHOLD_TRIGGERED',
        target: 'Swahili Chicken Biryani (MNC-003) dropped to 4 units',
        severity: 'WARNING' as const,
      },
      {
        id: `aud-${tenantId}-4`,
        timestamp: minAgo(120),
        actor: 'Grace Wanjiku',
        role: 'CASHIER',
        action: 'CASHIER_SHIFT_OPENED',
        target: 'Opening Float KES 5,000.00 on Main Store Drawer',
        severity: 'INFO' as const,
      },
    ];

    return { tables, orders, approvals, suppliers, recipes, auditLogs };
  }

  public listTables(tenantId: string): RestaurantTable[] {
    return [...this.getStore(tenantId).tables];
  }

  public updateTableStatus(
    tenantId: string,
    tableId: string,
    updates: {
      status: TableStatus;
      guests?: number;
      assignedWaiterName?: string | null;
    }
  ): RestaurantTable {
    const store = this.getStore(tenantId);
    const tbl = store.tables.find((t) => t.id === tableId);
    if (!tbl) throw new Error('Table not found');
    tbl.status = updates.status;
    if (updates.guests !== undefined) tbl.guests = updates.guests;
    if (updates.assignedWaiterName !== undefined) tbl.assignedWaiterName = updates.assignedWaiterName;
    if (updates.status === 'AVAILABLE') {
      tbl.guests = 0;
      tbl.activeOrderId = null;
      tbl.currentBillCents = 0;
    }
    tbl.updatedAt = new Date().toISOString();
    return { ...tbl };
  }

  public listOrders(tenantId: string): RestaurantOrder[] {
    return [...this.getStore(tenantId).orders].sort(
      (a, b) => new Date(b.submittedAt).getTime() - new Date(a.submittedAt).getTime()
    );
  }

  public submitTableOrder(
    tenantId: string,
    input: {
      tableId: string;
      waiterName: string;
      guests: number;
      items: Array<{
        variantId: string;
        productName: string;
        sku: string;
        quantity: number;
        unitPriceCents: number;
        notes: string;
      }>;
    }
  ): RestaurantOrder {
    const store = this.getStore(tenantId);
    const table = store.tables.find((t) => t.id === input.tableId);
    if (!table) throw new Error('Selected table not found');

    const subtotalCents = input.items.reduce((sum, i) => sum + i.unitPriceCents * i.quantity, 0);
    const orderNumber = `KOT-${Math.floor(2043 + Math.random() * 800)}`;
    const nowIso = new Date().toISOString();

    const orderItems: RestaurantOrderItem[] = input.items.map((item, idx) => ({
      id: `oi-${Date.now()}-${idx}`,
      ...item,
    }));

    const newOrder: RestaurantOrder = {
      id: `ord-${tenantId}-${Date.now()}`,
      orderNumber,
      tenantId,
      tableId: table.id,
      tableName: `${table.number} • ${table.name}`,
      waiterName: input.waiterName,
      guests: input.guests || table.guests || 2,
      status: 'SUBMITTED',
      items: orderItems,
      subtotalCents,
      taxCents: 0,
      totalCents: subtotalCents,
      submittedAt: nowIso,
      updatedAt: nowIso,
    };

    store.orders.unshift(newOrder);
    table.status = 'PREPARING';
    table.guests = newOrder.guests;
    table.assignedWaiterName = input.waiterName;
    table.activeOrderId = newOrder.id;
    table.currentBillCents = subtotalCents;
    table.updatedAt = nowIso;

    store.auditLogs.unshift({
      id: `aud-${Date.now()}`,
      timestamp: nowIso,
      actor: input.waiterName,
      role: 'WAITER',
      action: 'KITCHEN_ORDER_SUBMITTED',
      target: `${orderNumber} for ${table.number} (KES ${(subtotalCents / 100).toLocaleString()})`,
      severity: 'INFO',
    });

    return newOrder;
  }

  public advanceOrderStatus(
    tenantId: string,
    orderId: string,
    nextStatus: KitchenOrderStatus,
    actorName: string
  ): RestaurantOrder {
    const store = this.getStore(tenantId);
    const order = store.orders.find((o) => o.id === orderId);
    if (!order) throw new Error('Order not found');

    order.status = nextStatus;
    order.updatedAt = new Date().toISOString();

    const table = store.tables.find((t) => t.id === order.tableId);
    if (table) {
      if (nextStatus === 'PREPARING') table.status = 'PREPARING';
      else if (nextStatus === 'READY') table.status = 'READY';
      else if (nextStatus === 'SERVED') table.status = 'OCCUPIED';
      else if (nextStatus === 'AWAITING_PAYMENT') table.status = 'AWAITING_PAYMENT';
      else if (nextStatus === 'PAID' || nextStatus === 'CANCELLED') {
        table.status = 'AVAILABLE';
        table.guests = 0;
        table.activeOrderId = null;
        table.currentBillCents = 0;
      }
      table.updatedAt = order.updatedAt;
    }

    store.auditLogs.unshift({
      id: `aud-${Date.now()}`,
      timestamp: order.updatedAt,
      actor: actorName,
      role: 'STAFF',
      action: `ORDER_STATUS_${nextStatus}`,
      target: `${order.orderNumber} (${order.tableName})`,
      severity: 'INFO',
    });

    return { ...order };
  }

  public requestOrderCancellation(
    tenantId: string,
    orderId: string,
    waiterName: string,
    reason: string
  ): ManagerApprovalRequest {
    const store = this.getStore(tenantId);
    const order = store.orders.find((o) => o.id === orderId);
    if (!order) throw new Error('Order not found');

    order.cancellationRequested = true;
    order.cancellationReason = reason;
    order.updatedAt = new Date().toISOString();

    const req: ManagerApprovalRequest = {
      id: `apr-${Date.now()}`,
      tenantId,
      type: 'ORDER_CANCEL',
      requestedBy: waiterName,
      requesterRole: 'WAITER',
      referenceCode: order.orderNumber,
      amountCents: order.totalCents,
      reason,
      status: 'PENDING',
      createdAt: order.updatedAt,
    };

    store.approvals.unshift(req);
    store.auditLogs.unshift({
      id: `aud-${Date.now()}`,
      timestamp: order.updatedAt,
      actor: waiterName,
      role: 'WAITER',
      action: 'ORDER_CANCELLATION_REQUESTED',
      target: `${order.orderNumber} — Reason: ${reason}`,
      severity: 'WARNING',
    });

    return req;
  }

  public requestSaleReversalOrDiscount(
    tenantId: string,
    input: {
      type: 'DISCOUNT' | 'VOID_SALE' | 'REFUND';
      requestedBy: string;
      requesterRole: string;
      referenceCode: string;
      amountCents: number;
      reason: string;
    }
  ): ManagerApprovalRequest {
    const store = this.getStore(tenantId);
    const nowIso = new Date().toISOString();
    const req: ManagerApprovalRequest = {
      id: `apr-${Date.now()}`,
      tenantId,
      ...input,
      status: 'PENDING',
      createdAt: nowIso,
    };
    store.approvals.unshift(req);
    store.auditLogs.unshift({
      id: `aud-${Date.now()}`,
      timestamp: nowIso,
      actor: input.requestedBy,
      role: input.requesterRole,
      action: `${input.type}_APPROVAL_REQUESTED`,
      target: `${input.referenceCode} (KES ${(input.amountCents / 100).toLocaleString()}) — ${input.reason}`,
      severity: 'WARNING',
    });
    return req;
  }

  public listApprovals(tenantId: string): ManagerApprovalRequest[] {
    return [...this.getStore(tenantId).approvals];
  }

  public resolveApproval(
    tenantId: string,
    approvalId: string,
    decision: 'APPROVED' | 'REJECTED',
    managerName: string
  ): ManagerApprovalRequest {
    const store = this.getStore(tenantId);
    const item = store.approvals.find((a) => a.id === approvalId);
    if (!item) throw new Error('Approval request not found');

    item.status = decision;
    item.resolvedBy = managerName;
    item.resolvedAt = new Date().toISOString();

    if (decision === 'APPROVED' && item.type === 'ORDER_CANCEL') {
      const ord = store.orders.find((o) => o.orderNumber === item.referenceCode);
      if (ord) {
        this.advanceOrderStatus(tenantId, ord.id, 'CANCELLED', managerName);
      }
    }

    store.auditLogs.unshift({
      id: `aud-${Date.now()}`,
      timestamp: item.resolvedAt,
      actor: managerName,
      role: 'MANAGER',
      action: `APPROVAL_${decision}`,
      target: `${item.type} on ${item.referenceCode}`,
      severity: decision === 'APPROVED' ? 'WARNING' : 'INFO',
    });

    return { ...item };
  }

  public listSuppliers(tenantId: string): SupplierRecord[] {
    return [...this.getStore(tenantId).suppliers];
  }

  public addSupplier(
    tenantId: string,
    input: Omit<SupplierRecord, 'id' | 'tenantId' | 'status'>
  ): SupplierRecord {
    const store = this.getStore(tenantId);
    const sup: SupplierRecord = {
      id: `sup-${Date.now()}`,
      tenantId,
      ...input,
      status: 'ACTIVE',
    };
    store.suppliers.unshift(sup);
    return sup;
  }

  public listRecipes(tenantId: string): RecipeIngredientMapping[] {
    return [...this.getStore(tenantId).recipes];
  }

  public listAuditLogs(tenantId: string) {
    return [...this.getStore(tenantId).auditLogs];
  }
}

export const restaurantMockAdapter = new RestaurantMockAdapter();

